import { useState, useRef, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { useNavigate, Link, Navigate, useSearchParams } from "react-router-dom";
import { useGoogleLogin } from "@react-oauth/google";
import { Turnstile } from "@marsidev/react-turnstile";
import { api, formatErr } from "../lib/api";
import { GoogleLogo, CheckCircle, XCircle, CircleNotch } from "@phosphor-icons/react";
import { toast } from "sonner";
import SEOHead from "../components/SEOHead";
import AuthSplitLayout from "../components/AuthSplitLayout";
import { silentRestoreOrBackupKeyBundle } from "../lib/chatKeyRecovery";

const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;
// Preview/dev hosts can't run Cloudflare's widget (domain mismatch), so we send
// the backend's non-prod bypass token. Production (hacking.community) never
// matches this and has no bypass token set, so the real challenge always applies.
const IS_PREVIEW = typeof window !== "undefined"
    && /(\.preview\.emergentagent\.com|\.emergent\.host|localhost|127\.0\.0\.1)$/.test(window.location.hostname);
const PREVIEW_BYPASS_TOKEN = "qa_bypass_iter50";

/**
 * Sanitise the post-login redirect target.
 *
 * - Only allow same-origin relative paths starting with "/" (defeats open-redirect attacks).
 * - Forbid `/login` and `/register` to break loops.
 * - Cap length so a hostile querystring can't bloat the address bar.
 */
function safeNextPath(raw) {
    if (!raw) return null;
    const s = String(raw).slice(0, 200);
    if (!s.startsWith("/")) return null;
    if (s.startsWith("//")) return null;        // protocol-relative URL (open redirect)
    if (s.startsWith("/login") || s.startsWith("/register")) return null;
    return s;
}

export default function LoginPage() {
    const { user, setUser, refresh } = useAuth();
    const nav = useNavigate();
    const [searchParams] = useSearchParams();
    const nextPath = safeNextPath(searchParams.get("next"));
    const initialMode = searchParams.get("mode") === "register" ? "register" : "login";
    const [mode, setMode] = useState(initialMode);
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [username, setUsername] = useState("");
    // Live signup handle check: idle | checking | available | taken | reserved | invalid
    const [uStatus, setUStatus] = useState({ state: "idle", message: "", suggestions: [] });
    const [totpCode, setTotpCode] = useState("");
    const [needsTotp, setNeedsTotp] = useState(false);
    const [loading, setLoading] = useState(false);
    const [turnstileToken, setTurnstileToken] = useState(IS_PREVIEW ? PREVIEW_BYPASS_TOKEN : "");
    const [tsInteractive, setTsInteractive] = useState(false);
    const turnstileRef = useRef(null);

    const googleLogin = useGoogleLogin({
        // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
        // auth-code flow → backend exchanges the code with Google for an id_token.
        flow: "auth-code",
        onSuccess: async ({ code }) => {
            setLoading(true);
            try {
                const { data } = await api.post("/auth/google", { code });
                setUser(data);
                try { await refresh(); } catch { /* noop */ }
                toast.success(`Welcome, ${data.username}`);
                nav(nextPath || "/");
            } catch (err) {
                toast.error(formatErr(err));
            }
            setLoading(false);
        },
        onError: () => toast.error("Google sign-in was cancelled or blocked"),
    });

    // Debounced live availability check while typing a handle on signup.
    useEffect(() => {
        if (mode !== "register") { setUStatus({ state: "idle", message: "", suggestions: [] }); return; }
        const u = username.trim();
        if (!u) { setUStatus({ state: "idle", message: "", suggestions: [] }); return; }
        if (!/^[a-zA-Z0-9_]{3,24}$/.test(u)) {
            setUStatus({ state: "invalid", message: "3-24 chars · letters, numbers, underscore only", suggestions: [] });
            return;
        }
        setUStatus({ state: "checking", message: "checking…", suggestions: [] });
        const t = setTimeout(async () => {
            try {
                const { data } = await api.get("/auth/username-available", { params: { username: u } });
                if (data.available) setUStatus({ state: "available", message: "handle available", suggestions: [] });
                else setUStatus({ state: data.reason || "taken", message: data.message || "unavailable", suggestions: data.suggestions || [] });
            } catch { setUStatus({ state: "idle", message: "", suggestions: [] }); }
        }, 450);
        return () => clearTimeout(t);
    }, [username, mode]);

    if (user) return <Navigate to={nextPath || "/"} replace />;

    const submit = async (e) => {
        e.preventDefault();
        // Gate submission on Turnstile if it's configured. Empty token = widget hasn't solved yet.
        if (TURNSTILE_SITE_KEY && !turnstileToken) {
            toast.error("Please complete the bot check");
            return;
        }
        if (mode === "register" && uStatus.state !== "available") {
            toast.error(
                uStatus.state === "reserved" ? "That handle is reserved — pick another"
                : uStatus.state === "blocked" ? "That handle isn't allowed — pick another"
                : uStatus.state === "taken" ? "That handle is already taken"
                : uStatus.state === "invalid" ? "Handle must be 3-24 chars (letters, numbers, underscore)"
                : "Pick an available handle first"
            );
            return;
        }
        setLoading(true);
        try {
            const path = mode === "login" ? "/auth/login" : "/auth/register";
            const base = mode === "login"
                ? { email, password, ...(needsTotp ? { totp_code: totpCode.trim() } : {}) }
                : { email, password, username, account_type: "hacker" };
            const payload = TURNSTILE_SITE_KEY ? { ...base, turnstile_token: turnstileToken } : base;
            const { data } = await api.post(path, payload);
            // iter167k — WHILE the password is still in scope, silently
            // restore (or upload, first-time) the encrypted chat-key bundle.
            // Fire-and-forget: any failure here must not block login.
            silentRestoreOrBackupKeyBundle(data?.user_id, password).catch(() => {});
            // Optimistic local set so the redirect happens fast…
            setUser(data);
            // First-run welcome strip (home feed) — only for brand-new signups.
            if (mode === "register") { try { localStorage.setItem("hc_show_welcome", "1"); } catch { /* noop */ } }
            // …but also re-run AuthContext.refresh() to (a) re-validate against /auth/me and
            // (b) drain any pending referral attribution stored in localStorage from ?ref=<username>.
            try { await refresh(); } catch { /* noop — setUser above keeps us logged in */ }
            toast.success(`Welcome${mode === "register" ? "" : " back"}, ${data.username}`);
            // iter163c — the /login page is hacker-only now.
            nav(nextPath || "/");
        } catch (err) {
            const detail = err?.response?.data?.detail;
            const code = typeof detail === "object" ? detail?.code : null;
            const reason = typeof detail === "object" ? detail?.reason : null;
            if (code === "turnstile_failed" || code === "turnstile_unavailable") {
                toast.error("Bot check failed — please try again");
            } else if (reason === "totp_required") {
                setNeedsTotp(true);
                toast.info("2FA required — enter your authenticator code");
            } else if (reason === "totp_invalid") {
                toast.error("Invalid 2FA code — try again");
            } else {
                toast.error(formatErr(err));
                if (needsTotp) setTotpCode("");
            }
            // Always reset Turnstile on any failure — tokens are single-use.
            if (turnstileRef.current) { try { turnstileRef.current.reset(); } catch (_e) { /* noop */ } }
            setTurnstileToken(IS_PREVIEW ? PREVIEW_BYPASS_TOKEN : "");
        }
        setLoading(false);
    };

    return (
        <AuthSplitLayout tone="cyan" testid="login-page-shell">
            <SEOHead
                title={mode === "register" ? "Create your account" : "Sign in"}
                description="Sign in to hacking.community — the home for hackers and CTF players."
                path={mode === "register" ? "/login?mode=register" : "/login"}
                noindex
            />

            <div className="w-full" data-testid="auth-card">
                    <h1 className="text-3xl sm:text-4xl font-black tracking-tight text-white leading-tight" style={{ fontFamily: "'Space Grotesk', 'Chivo', sans-serif" }}>
                        {mode === "login" ? <>Welcome back!<br />Login to your account</> : <>Join the community.<br />Create your account</>}
                    </h1>
                    <p className="text-sm text-slate-400 mt-3 mb-7">
                        {mode === "login" ? "It's nice to see you again. Ready to hack?" : "Free forever — academy, labs, CTFs, bounties and more."}
                    </p>

                    <button
                        type="button"
                        onClick={() => googleLogin()}
                        disabled={loading}
                        className="w-full bg-slate-900 border border-slate-700 hover:border-cyan-500/50 text-slate-200 text-sm py-3 rounded-md flex items-center justify-center gap-2 mb-4 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-mono"
                        data-testid="google-login-btn"
                    >
                        <GoogleLogo size={16} weight="bold" /> Continue with Google
                    </button>

                    <div className="flex items-center gap-3 mb-4">
                        <div className="flex-1 h-px bg-slate-800" />
                        <div className="text-[10px] uppercase tracking-widest text-slate-600">or</div>
                        <div className="flex-1 h-px bg-slate-800" />
                    </div>

                    <form onSubmit={submit} className="space-y-3">
                        {mode === "register" && (
                            <div>
                                <label htmlFor="signup-username" className="text-[10px] uppercase tracking-widest text-slate-500 mb-1 block">Username</label>
                                <div className="relative">
                                    <input
                                        required
                                        id="signup-username"
                                        name="username"
                                        value={username}
                                        onChange={(e) => setUsername(e.target.value)}
                                        className={`w-full bg-[#0b0f19] border text-slate-200 text-sm font-mono p-3 pr-10 rounded-md focus:ring-1 focus:outline-none transition-colors ${
                                            uStatus.state === "available" ? "border-emerald-500/60 focus:border-emerald-500 focus:ring-emerald-500/30"
                                            : ["taken", "reserved", "invalid", "blocked"].includes(uStatus.state) ? "border-rose-500/60 focus:border-rose-500 focus:ring-rose-500/30"
                                            : "border-white/10 focus:border-cyan-500 focus:ring-cyan-500/30"
                                        }`}
                                        placeholder="ph4nt0m_h4x"
                                        data-testid="username-input"
                                        autoComplete="username"
                                    />
                                    <div className="absolute right-3 top-1/2 -translate-y-1/2" data-testid="username-status-icon">
                                        {uStatus.state === "checking" && <CircleNotch size={16} className="animate-spin text-slate-500" />}
                                        {uStatus.state === "available" && <CheckCircle size={16} weight="fill" className="text-emerald-400" />}
                                        {["taken", "reserved", "invalid", "blocked"].includes(uStatus.state) && <XCircle size={16} weight="fill" className="text-rose-400" />}
                                    </div>
                                </div>
                                {uStatus.state !== "idle" && (
                                    <div className={`mt-1 text-[10px] font-mono ${
                                        uStatus.state === "available" ? "text-emerald-400"
                                        : uStatus.state === "checking" ? "text-slate-500"
                                        : "text-rose-400"
                                    }`} data-testid="username-status-text">
                                        // {uStatus.message}
                                    </div>
                                )}
                                {uStatus.suggestions.length > 0 && (
                                    <div className="mt-2 flex flex-wrap items-center gap-1.5" data-testid="username-suggestions">
                                        <span className="text-[10px] font-mono text-slate-500">// try:</span>
                                        {uStatus.suggestions.map((s) => (
                                            <button
                                                key={s}
                                                type="button"
                                                onClick={() => setUsername(s)}
                                                data-testid={`username-suggestion-${s}`}
                                                className="text-[11px] font-mono text-cyan-300 border border-cyan-500/30 hover:border-cyan-400 hover:bg-cyan-500/10 px-2 py-0.5 rounded-full transition-colors"
                                            >
                                                {s}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                        <div>
                            <label htmlFor="auth-email" className="text-[10px] uppercase tracking-widest text-slate-500 mb-1 block">Email</label>
                            <input
                                type="email"
                                required
                                id="auth-email"
                                name="email"
                                autoComplete="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="w-full bg-[#0b0f19] border border-white/10 text-slate-200 text-sm font-mono p-3 rounded-md focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500/30 focus:outline-none"
                                placeholder="you@example.com"
                                data-testid="email-input"
                            />
                        </div>
                        <div>
                            <label htmlFor="auth-password" className="text-[10px] uppercase tracking-widest text-slate-500 mb-1 block">Password</label>
                            <input
                                type="password"
                                required
                                id="auth-password"
                                name="password"
                                autoComplete={mode === "register" ? "new-password" : "current-password"}
                                minLength={6}
                                aria-describedby={mode === "register" ? "password-req" : undefined}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="w-full bg-[#0b0f19] border border-white/10 text-slate-200 text-sm font-mono p-3 rounded-md focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500/30 focus:outline-none"
                                placeholder="••••••••"
                                data-testid="password-input"
                            />
                            {mode === "register" && (
                                <p id="password-req" className="text-[10px] text-slate-400 font-mono mt-1">// at least 6 characters — longer passphrases are stronger</p>
                            )}
                        </div>
                        {needsTotp && mode === "login" && (
                            <div data-testid="totp-field">
                                <label htmlFor="auth-totp" className="text-[10px] uppercase tracking-widest text-cyan-400 mb-1 block">2FA Code</label>
                                <input
                                    required
                                    id="auth-totp"
                                    name="totp"
                                    autoComplete="one-time-code"
                                    inputMode="numeric"
                                    value={totpCode}
                                    onChange={(e) => setTotpCode(e.target.value)}
                                    placeholder="000000"
                                    maxLength={6}
                                    autoFocus
                                    className="w-full bg-[#0b0f19] border border-cyan-500/40 text-cyan-300 text-lg font-mono p-3 rounded-md text-center tracking-[0.4em] focus:border-cyan-400 focus:outline-none"
                                    data-testid="totp-input"
                                />
                                <p className="text-[10px] text-slate-500 font-mono mt-1">// 6-digit code from your authenticator app</p>
                            </div>
                        )}
                        {/* Turnstile in interaction-only mode: renders a 0×0 invisible div
                          * for trusted browsers (silent token issuance), or pops up an
                          * interstitial dialog if Cloudflare flags the visitor as suspicious.
                          * Wrapper stays in the layout flow so the iframe can initialize.
                          * 99%+ of real users see nothing. */}
                        {TURNSTILE_SITE_KEY && !IS_PREVIEW && (
                            <div className={`cf-turnstile-mount w-full overflow-hidden rounded-md [&>div]:!w-full [&>div]:justify-center [&_iframe]:!w-full ${tsInteractive ? "bg-[#0b0f19] border border-white/10 p-1.5" : ""}`} data-testid="turnstile-widget">
                                <Turnstile
                                    ref={turnstileRef}
                                    siteKey={TURNSTILE_SITE_KEY}
                                    onSuccess={(token) => setTurnstileToken(token)}
                                    onError={() => setTurnstileToken("")}
                                    onExpire={() => setTurnstileToken("")}
                                    onBeforeInteractive={() => setTsInteractive(true)}
                                    options={{
                                        theme: "dark",
                                        size: "flexible",
                                        retry: "auto",
                                        refreshExpired: "auto",
                                        appearance: "interaction-only",
                                    }}
                                />
                            </div>
                        )}
                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full bg-cyan-400 hover:bg-cyan-300 text-slate-950 font-bold text-xs uppercase tracking-widest py-3 rounded-md transition-colors disabled:opacity-50"
                            data-testid="auth-submit-btn"
                        >
                            {loading ? "…" : mode === "login" ? (needsTotp ? "Verify & sign in" : "Sign in") : "Create account"}
                        </button>
                        {TURNSTILE_SITE_KEY && (
                            <div
                                className="flex items-center justify-center gap-1.5 text-[10px] font-mono text-slate-500 -mt-1"
                                data-testid="turnstile-chip"
                                title={turnstileToken ? "Verified — ready to submit" : "Cloudflare is silently verifying your browser"}
                            >
                                <span
                                    className={`inline-block w-1 h-1 rounded-full ${turnstileToken ? "bg-emerald-400" : "bg-cyan-400 animate-pulse"}`}
                                    aria-hidden="true"
                                />
                                <span className="opacity-70">{IS_PREVIEW ? "preview mode —" : "protected by"}</span>
                                <span className={turnstileToken ? "text-emerald-400" : "text-cyan-400/70"}>{IS_PREVIEW ? "turnstile bypassed" : "cloudflare turnstile"}</span>
                            </div>
                        )}
                    </form>

                    {mode === "register" && (
                        <div className="mt-4 flex items-center justify-center gap-2 text-[10px] font-mono uppercase tracking-widest text-slate-500" data-testid="privacy-trust-line">
                            <span className="inline-block w-1 h-1 rounded-full bg-emerald-400" aria-hidden="true" />
                            <span>opt-in analytics</span>
                            <span className="text-slate-700">·</span>
                            <span>e2e encrypted dms</span>
                            <span className="text-slate-700">·</span>
                            <span>no ad trackers</span>
                        </div>
                    )}

                    <div className="mt-5 text-center text-xs text-slate-500">
                        {mode === "login" ? "New to hacking.community?" : "Already have an account?"}{" "}
                        <button
                            type="button"
                            onClick={() => setMode(mode === "login" ? "register" : "login")}
                            className="text-cyan-400 hover:text-cyan-300 uppercase tracking-widest px-2 py-3 -my-3 min-h-[44px] inline-flex items-center"
                            data-testid="toggle-mode-btn"
                        >
                            {mode === "login" ? "Register →" : "Sign in →"}
                        </button>
                    </div>

                    {mode === "login" && (
                        <div className="mt-2 text-center text-xs">
                            <Link to="/forgot-password" className="text-slate-500 hover:text-cyan-400 uppercase tracking-widest" data-testid="forgot-password-link">
                                Forgot password?
                            </Link>
                        </div>
                    )}
            </div>
        </AuthSplitLayout>
    );
}
